"use client";

import React from 'react';
import { LogOut, LayoutGrid, Clock, FolderTree, History, Settings, ChevronLeft, ChevronRight, Plus, Trash2, Video, CheckCircle, Loader2 } from 'lucide-react';
import { Group } from '@/types/group.interface';
import { DraggableGroupItem } from "@/components/studio/DraggableGroupItem";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Api from '@/lib/api';
import { Project } from '@/types/project.interface';
import { useModal } from '@/redux/useModal';
import { arrayMove } from "@dnd-kit/sortable";

interface StudioSidebarProps {
    groups: Group[];
    setGroups: React.Dispatch<React.SetStateAction<Group[]>>;
    currentGroupId?: string;
    onSelect: (id: string, silent?: boolean) => Promise<void>;
    profiles: Project[];
    onOpenSettings: () => void;
    onViewResult?: (group: Group) => void;
}

export const StudioSidebar: React.FC<StudioSidebarProps> = ({
    groups,
    setGroups,
    currentGroupId,
    onSelect,
    profiles,
    onOpenSettings,
    onViewResult,
}) => {
    const modaler = useModal();
    const [activeTab, setActiveTab] = React.useState<'active' | 'projects'>('active');
    const trackers = {
        switchGroup: onSelect
    };

    const handleGroupDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = groups.findIndex((g) => g.id === active.id);
        const newIndex = groups.findIndex((g) => g.id === over.id);
        const newOrder = arrayMove(groups, oldIndex, newIndex);
        setGroups(newOrder);
        try {
            await Api.groups.reorder(newOrder.map((g, index) => ({ id: g.id, order: index })));
        } catch (error) { console.error("Failed to sync group order:", error); }
    };

    const onCreateGroup = async () => {
        try {
            const newGroup = await Api.groups.create({
                name: `未命名创作 ${groups.length + 1}`,
                projectId: profiles.length > 0 ? profiles[0].id : null
            });
            setGroups(prev => [newGroup, ...prev]);
            await trackers.switchGroup(newGroup.id);
        } catch (error) { console.error("创建组失败:", error); }
    };

    const handleDownload = async (key: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        try {
            const response = await fetch(`/api/download?key=${encodeURIComponent(key)}`);
            if (!response.ok) throw new Error('Download failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = key.split('/').pop() || 'video.mp4';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Download error:', error);
            alert('文件下载失败');
        }
    };

    const onDeleteGroup = async (groupId: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (!await modaler.confirm("确定删除此组及其所有片段和素材吗？")) return;
        try {
            await Api.groups.delete(groupId);
            const remaining = groups.filter(g => g.id !== groupId);
            setGroups(remaining);
            if (currentGroupId === groupId) {
                if (remaining.length > 0) await trackers.switchGroup(remaining[0].id);
                else {
                    // Logic to clear current group should be handled by parent or via onSelect(null)
                    // For now, we just pass to parent
                }
            }
        } catch (error) { console.error("删除组失败:", error); }
    };
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const activeGroups = groups.filter(g => g.status !== "completed");
    const historyGroups = groups.filter(g => g.status === "completed");

    return (
        <aside className="w-60 bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-800 flex flex-col shrink-0 transition-colors">
            <div className="p-6 border-b border-zinc-200 dark:border-zinc-800 flex justify-between items-center bg-zinc-50/50 dark:bg-transparent">
                {/* <button onClick={onOpenSettings} className="p-2 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-all">
                    <Settings size={18} />
                </button> */}
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 custom-scrollbar">
                <div className="space-y-2">
                    <div className="text-xs font-semibold text-zinc-400 dark:text-zinc-500 px-2 flex justify-between items-center uppercase tracking-wider">
                        <span>活动创作</span>
                        <button onClick={onCreateGroup} className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                            <Plus size={16} />
                        </button>
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
                        <SortableContext items={activeGroups.map(g => g.id)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-1">
                                {activeGroups.map((group) => (
                                    <DraggableGroupItem
                                        key={group.id}
                                        group={group}
                                        isActive={currentGroupId === group.id}
                                        onSelect={() => onSelect(group.id)}
                                        onDelete={() => onDeleteGroup(group.id)}
                                        onViewResult={() => onSelect(group.id)} // Direct selection for result
                                    />
                                ))}
                                {activeGroups.length === 0 && (
                                    <div className="px-3 py-4 text-center border border-dashed border-zinc-200 dark:border-zinc-800 rounded-none">
                                        <p className="text-[10px] text-zinc-500 uppercase font-bold">暂无活动创作</p>
                                    </div>
                                )}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>

                <div className="border-t border-zinc-200 dark:border-zinc-800 p-2">
                    <div className="flex gap-1 p-1 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-none mb-4">
                        {(['active', 'projects'] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-[9px] font-black uppercase tracking-widest transition-all rounded-none
                                    ${activeTab === tab
                                        ? 'bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400 border border-zinc-200 dark:border-zinc-700'
                                        : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200'}`}
                            >
                                {tab === 'active' ? <Clock size={10} /> : <FolderTree size={10} />}
                                {tab === 'active' ? '活动' : '项目'}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="text-xs font-semibold text-zinc-500 px-2 flex items-center gap-2 uppercase tracking-wider">
                        <History size={12} />
                        <span>历史记录</span>
                    </div>
                    <div className="space-y-1">
                        {historyGroups.map(group => (
                            <DraggableGroupItem
                                key={group.id}
                                group={group}
                                isActive={currentGroupId === group.id}
                                onSelect={() => onSelect(group.id)}
                                onDelete={() => onDeleteGroup(group.id)}
                                onViewResult={() => onViewResult?.(group)}
                            />
                        ))}
                        {historyGroups.length === 0 && (
                            <div className="px-3 py-6 text-center">
                                <p className="text-xs text-zinc-700 italic">暂无历史记录</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </aside>
    );
};
